import React, { FC, MouseEvent } from 'react';
import styles from '../journey-explorer.module.scss';
import { COLORS } from '../journey-explorer-utils';
import { BOX_HEIGHT, MapNode, boxLabel } from '../journey-map-layout';
import { formatPct } from '../journey-compare';

interface MapInteractionProps {
	node: MapNode;
	label: string;
	border: number;
	highlighted: boolean;
	faded: boolean;
	dragging: boolean;
	showGoal: boolean;
	compareA?: number | null;
	compareB?: number | null;
	onPress: (node: MapNode, event: MouseEvent<SVGGElement>) => void;
	onSelect: (node: MapNode) => void;
	onHover: (name: string | null) => void;
}

const MapInteraction: FC<MapInteractionProps> = ({
	node,
	label,
	border,
	highlighted,
	faded,
	dragging,
	showGoal,
	compareA,
	compareB,
	onPress,
	onSelect,
	onHover,
}) => {
	const { x, y, width, info, stat } = node;
	const comparing = compareA !== undefined;
	const barWidth = width - (comparing ? 62 : 42);

	return (
		<g
			opacity={faded ? 0.35 : 1}
			style={{ cursor: dragging ? 'grabbing' : 'pointer' }}
			onMouseEnter={() => onHover(info.name)}
			onMouseLeave={() => onHover(null)}
			onMouseDown={(event) => {
				event.stopPropagation();
				if (event.button === 0) {
					onPress(node, event);
				}
			}}
			onClick={(event) => {
				event.stopPropagation();
				onSelect(node);
			}}
		>
			<rect
				x={x}
				y={y}
				width={width}
				height={BOX_HEIGHT}
				rx={8}
				className={styles.map_node}
				fill={highlighted ? COLORS.card : COLORS.raised}
				stroke={highlighted ? COLORS.text : info.color}
				strokeOpacity={highlighted ? 1 : 0.6}
				strokeWidth={highlighted ? Math.max(border, 1.8) : border}
			/>
			<title>{info.label}</title>
			<text x={x + width / 2} y={y + 18} textAnchor="middle" fill={COLORS.text} fontSize={12} fontWeight={500}>
				{boxLabel(info.label)}
			</text>
			<text x={x + width / 2} y={y + 31} textAnchor="middle" fill={COLORS.muted} fontSize={10}>
				{label}
			</text>
			<rect
				x={x + 8}
				y={y + BOX_HEIGHT - 10}
				width={barWidth}
				height={3}
				rx={1.5}
				fill={COLORS.line}
				opacity={0.6}
			/>
			{showGoal && (
				<rect
					x={x + 8}
					y={y + BOX_HEIGHT - 10}
					width={Math.max(2, (barWidth * stat.reached_pct) / 100)}
					height={3}
					rx={1.5}
					fill={COLORS.goal}
					opacity={0.8}
				/>
			)}
			{comparing ? (
				<text x={x + width - 7} y={y + BOX_HEIGHT - 6} textAnchor="end" fontSize={9} fontWeight={700}>
					<tspan fill={COLORS.compareA}>{formatPct(compareA)}</tspan>
					<tspan fill={COLORS.dim}> · </tspan>
					<tspan fill={COLORS.compareB}>{formatPct(compareB)}</tspan>
				</text>
			) : (
				showGoal && (
					<text
						x={x + width - 7}
						y={y + BOX_HEIGHT - 6}
						textAnchor="end"
						fill={COLORS.goal}
						fontSize={9}
						fontWeight={600}
						opacity={0.9}
					>
						{stat.reached_pct.toFixed(0)}%
					</text>
				)
			)}
		</g>
	);
};

export default React.memo(MapInteraction);
